// src/ui/screens/post/AtomViewScreen.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { VideoItem } from '../../../components/PostComponents/VideoItem';

// Matches a typical phone viewport width, so the desktop view shows the
// atom at the same width it renders on mobile instead of stretching full-bleed.
const ATOM_MAX_WIDTH = 430;

export const AtomViewScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const listings = location.state?.listings || [];
  const initialIndex = location.state?.initialIndex || 0;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchEndY, setTouchEndY] = useState(0);
  const containerRef = useRef(null);

  // ── Handle keyboard navigation ────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp' && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (e.key === 'ArrowDown' && currentIndex < listings.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else if (e.key === 'Escape') {
        navigate(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, listings.length, navigate]);

  // ── Touch handlers for swipe ──────────────────────────────────────────────

  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEndY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    const diff = touchStartY - touchEndY;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentIndex < listings.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else if (diff < 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  // ── Handle scroll with wheel ──────────────────────────────────────────────

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY > 0 && currentIndex < listings.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (e.deltaY < 0 && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // ── Go back ───────────────────────────────────────────────────────────────

  const handleBack = () => {
    navigate(-1);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!listings || listings.length === 0) {
    return (
      <div className="min-h-screen bg-filatom-bg flex items-center justify-center">
        <p className="text-white/50">No atoms available</p>
      </div>
    );
  }

  const currentListing = listings[currentIndex];

  return (
    // Outer viewport: on mobile this is just the video edge-to-edge (unchanged).
    // On desktop it becomes a letterbox background behind a capped-width column.
    <div className="fixed inset-0 bg-black overflow-hidden flex items-center justify-center">
      <div
        ref={containerRef}
        className="relative h-full w-full md:h-[calc(100%-32px)] md:max-h-[900px] md:shadow-2xl md:shadow-black/60 overflow-hidden"
        style={{ maxWidth: '100%' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
      >
        <div
          className="relative h-full w-full bg-filatom-bg mx-auto md:rounded-2xl overflow-hidden"
          style={{ maxWidth: `${ATOM_MAX_WIDTH}px` }}
        >
          {/* Full-screen video container */}
          <div className="absolute inset-0">
            <VideoItem
              key={currentListing.id || currentIndex}
              listing={currentListing}
              isActive={true}
              bottomNavBarHeight={0}
            />
          </div>

          {/* Back button - top left */}
          <div
            className="absolute top-4 left-3 z-10"
            style={{ paddingTop: window.navigator?.userAgent?.includes('iPhone') ? 'env(safe-area-inset-top)' : 0 }}
          >
            <button
              onClick={handleBack}
              className="w-[38px] h-[38px] rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>

          {/* Page indicator */}
          {listings.length > 1 && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-1.5">
              {listings.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'h-3 bg-filatom-primary'
                      : 'h-1.5 bg-white/30'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Progress indicator - bottom */}
          {listings.length > 1 && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-0.5 z-10 px-4">
              {listings.map((_, index) => (
                <div
                  key={index}
                  className={`h-0.5 flex-1 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-filatom-primary'
                      : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AtomViewScreen;